import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Trash2, LogOut, FileDown, CalendarDays, ChevronDown, Mail, Phone } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import PageSeo from "@/components/PageSeo";
import type { Tables } from "@/integrations/supabase/types";

type Application = Tables<"job_applications">;

const STATUSES = ["neu", "in Prüfung", "eingeladen", "abgelehnt"];

const AdminApplications = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) navigate("/admin", { replace: true });
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate("/admin", { replace: true });
    });
    fetchApplications();
    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchApplications = async () => {
    const { data, error } = await supabase
      .from("job_applications")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
      return;
    }
    setApplications(data ?? []);
  };

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("job_applications").update({ status }).eq("id", id);
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
      return;
    }
    setApplications((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
  };

  const remove = async (app: Application) => {
    if (!confirm(`Bewerbung von ${app.name} wirklich löschen?`)) return;
    if (app.cv_path) {
      await supabase.storage.from("job-applications").remove([app.cv_path]);
    }
    const { error } = await supabase.from("job_applications").delete().eq("id", app.id);
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Bewerbung gelöscht" });
    setApplications((prev) => prev.filter((a) => a.id !== app.id));
  };

  const openCv = async (path: string) => {
    const { data, error } = await supabase.storage
      .from("job-applications")
      .createSignedUrl(path, 300);
    if (error || !data?.signedUrl) {
      toast({ title: "Fehler", description: "Datei konnte nicht geöffnet werden.", variant: "destructive" });
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const logout = async () => {
    await supabase.auth.signOut();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <PageSeo title="Bewerbungen – Admin" description="Verwaltung der Online-Bewerbungen" path="/admin/applications" noindex />
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between gap-2 mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-primary">Bewerbungen</h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/events")}>
              <CalendarDays className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">Events</span>
            </Button>
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">Logout</span>
            </Button>
          </div>
        </div>

        {applications.length === 0 && (
          <p className="text-muted-foreground text-sm">Noch keine Bewerbungen eingegangen.</p>
        )}

        <div className="space-y-3">
          {applications.map((app) => (
            <Collapsible
              key={app.id}
              open={openId === app.id}
              onOpenChange={(open) => setOpenId(open ? app.id : null)}
              className="border border-border rounded-lg bg-card"
            >
              <CollapsibleTrigger className="w-full text-left p-3 sm:p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-foreground break-words">{app.name}</p>
                  <p className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-2">
                    <span>{app.position}</span>
                    <span>·</span>
                    <span>{format(new Date(app.created_at), "dd.MM.yyyy", { locale: de })}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] uppercase tracking-wide px-2 py-1 rounded bg-primary/10 text-primary">
                    {app.status}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent className="px-3 sm:px-4 pb-4 space-y-3 text-sm">
                <div className="grid sm:grid-cols-2 gap-2 text-foreground/90">
                  <a href={`mailto:${app.email}`} className="flex items-center gap-2 hover:text-primary break-all">
                    <Mail className="w-4 h-4 shrink-0" /> {app.email}
                  </a>
                  <a href={`tel:${app.phone}`} className="flex items-center gap-2 hover:text-primary">
                    <Phone className="w-4 h-4 shrink-0" /> {app.phone}
                  </a>
                </div>
                <p><span className="text-muted-foreground">Volljährig:</span> {app.is_adult ? "Ja" : "Nein"}</p>
                <p><span className="text-muted-foreground">Verfügbarkeit:</span> {app.availability?.join(", ") || "–"}</p>
                <p><span className="text-muted-foreground">Erfahrung:</span> {app.experience}</p>
                <p><span className="text-muted-foreground">Start:</span> {app.start_date || "–"}</p>
                {app.message && (
                  <p className="whitespace-pre-wrap">
                    <span className="text-muted-foreground">Nachricht:</span> {app.message}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <select
                    value={app.status}
                    onChange={(e) => updateStatus(app.id, e.target.value)}
                    className="h-9 px-2 rounded border border-border bg-background text-sm"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {app.cv_path && (
                    <Button size="sm" variant="outline" onClick={() => openCv(app.cv_path!)}>
                      <FileDown className="w-4 h-4 mr-2" /> Lebenslauf
                    </Button>
                  )}
                  <Button size="sm" variant="destructive" onClick={() => remove(app)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminApplications;
